import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { Response, Pagination } from '@ui/src';
import {
  RiskScoreItem,
  SourceOfFunds,
  SourceOfFundsValidators,
} from '../../../types';
import { individualClientsRiskApi } from '../services';
import ROUTES from '../../../../routes';

export interface IndividualClientsSourceOfFundsListState {
  values?: SourceOfFunds[];
  pagination?: Pagination;
  selectedList: string[];
  validators?: SourceOfFundsValidators;
  errorsList?: string;
}

const initialState: IndividualClientsSourceOfFundsListState = {
  values: undefined,
  selectedList: [],
};

export const individualClientsSourceOfFundsSlice = createSlice({
  name: 'individualClientsSourceOfFunds',
  initialState,
  reducers: {
    selectList: (state, action: PayloadAction<string[]>) => {
      state.selectedList = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      individualClientsRiskApi.endpoints.getSourceOfFundsValidators
        .matchFulfilled,
      (state, action: PayloadAction<{ data: SourceOfFundsValidators }>) => {
        state.validators = action.payload.data;
      },
    );
    builder.addMatcher(
      individualClientsRiskApi.endpoints.getSourceOfFundsList.matchPending,
      (state) => {
        state.errorsList = undefined;
      },
    );
    builder.addMatcher(
      individualClientsRiskApi.endpoints.getSourceOfFundsList.matchRejected,
      (state, action: any) => {
        state.values = undefined;
        state.pagination = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.errorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.errorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      individualClientsRiskApi.endpoints.getSourceOfFundsList.matchFulfilled,
      (state, action: PayloadAction<Response<RiskScoreItem>>) => {
        state.values = action.payload.data.edges
          .map((item) => ({
            id: item.node.id,
            riskScore: item.node.riskScore,
            name: item.node.name,
            scoreAction: item.node.riskScoreRange.action,
            riskCategory: item.node.riskScoreRange.name,
            riskScoreRangeId: item.node.riskScoreRangeId,
            url: generatePath(
              ROUTES.INDIVIDUAL_CLIENTS_RISK_SOURCE_OF_FUNDS_EDIT,
              {
                sourceId: item.node.id,
              },
            ),
          }))
          .sort((a, b) => b.riskScore - a.riskScore);

        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      individualClientsRiskApi.endpoints.updateSourceOfFunds.matchFulfilled,
      (state, action: PayloadAction<{ data: RiskScoreItem }>) => {
        const { data } = action.payload;

        const nextList = state.values?.map((item) => {
          let result = { ...item };

          if (result.id === data.id) {
            result = {
              id: data.id,
              riskScore: data.riskScore,
              name: data.name,
              scoreAction: data.riskScoreRange.action,
              riskCategory: data.riskScoreRange.name,
              riskScoreRangeId: data.riskScoreRangeId,
              url: generatePath(
                ROUTES.INDIVIDUAL_CLIENTS_RISK_SOURCE_OF_FUNDS_EDIT,
                {
                  sourceId: data.id,
                },
              ),
            };
          }

          return result;
        });

        state.values = nextList;
      },
    );
  },
});

export const { selectList } = individualClientsSourceOfFundsSlice.actions;

export default individualClientsSourceOfFundsSlice.reducer;
